// Provider for the comms feature's persistent WebSocket, bound to the browser.
//
// The live layer itself is the shared `useChatConnection`; this supplies what is the
// browser's: how a socket opens (the session cookie rides the upgrade), where
// read acks wait (localStorage), how a pending file is uploaded (a Blob with
// its name), and how a held file is previewed (an object URL, revoked when
// let go). Watched-ness is the browser socket's own, from tab visibility.

import type { ReactNode } from 'react';
import { useAuth } from '@contexts/useAuth';
import { ChatContext, useChatConnection, useLocalAttachments, useUnread, type ILiveSocket } from '@d-orca/shared/client/hooks';
import type { ChatSocketHandlers } from '@d-orca/shared/client/comms';
import { uploadAttachment } from '@apis/comms';
import type { IPendingAttachment } from '../hooks/useComposerAttachments';
import { ChatSocket } from './ChatSocket';
import { readQueueStore } from './storage';

const createSocket = (siteId: string, handlers: ChatSocketHandlers): ILiveSocket => new ChatSocket(siteId, handlers);
// A voice blob still being fetched from the recorder cannot be sent yet: the
// upload rejects and the row fails, keeping its files for Retry.
const upload = (siteId: string, channelId: string, file: IPendingAttachment) =>
  file.blob
    ? uploadAttachment(siteId, channelId, file.blob, file.name, file.durationMs)
    : Promise.reject(new Error('attachment not ready'));

// The composer frees its own previews as soon as it sends, so every held file
// gets a preview URL owned here, freed when the message is confirmed or discarded.
const preview = (file: IPendingAttachment): IPendingAttachment => ({
  ...file,
  previewUrl: file.blob && file.kind !== 'file' ? URL.createObjectURL(file.blob) : undefined,
});

const free = (file: IPendingAttachment) => {
  if (file.previewUrl) URL.revokeObjectURL(file.previewUrl);
};

interface IChatSocketProviderProps {
  siteId: string;
  children: ReactNode;
}

export function ChatSocketProvider({ siteId, children }: IChatSocketProviderProps) {
  const { user } = useAuth();
  const { incrementUnread } = useUnread();
  const local = useLocalAttachments({ preview, free });
  const value = useChatConnection({
    siteId,
    currentUser: user,
    createSocket,
    readQueueStore,
    local,
    upload,
    onIncomingMessage: incrementUnread,
  });
  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}
